import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Title
);

export default function TableNo13() {
  const pdfRef = useRef();

  // ✅ Data
  const raceData = [
    { race: "American Indian or Alaska Native Alone", change: 0.07 },
    { race: "Asian Alone", change: -0.31 },
    { race: "Black or African American Alone", change: 1.02 },
    { race: "Native Hawaiian or Other Pacific Islander Alone", change: -0.16 },
    { race: "Two or More Race Groups", change: 0.48 },
    { race: "White Alone", change: -1.1 },
  ];

  // ✅ Derived insights
  const topIncrease = raceData.reduce((a, b) => (a.change > b.change ? a : b));
  const topDecrease = raceData.reduce((a, b) => (a.change < b.change ? a : b));
  const avgChange = (
    raceData.reduce((sum, r) => sum + r.change, 0) / raceData.length
  ).toFixed(2);

  // ✅ Chart setup
  const chartData = {
    labels: raceData.map((r) => r.race),
    datasets: [
      {
        label: "Percentage Change (%)",
        data: raceData.map((r) => r.change),
        borderColor: "#993366",
        backgroundColor: "rgba(153, 51, 102, 0.2)",
        pointBackgroundColor: raceData.map((r) =>
          r.change >= 0 ? "#22C55E" : "#EF4444"
        ),
        pointBorderColor: "#fff",
        pointRadius: 6,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Percentage Change in Employment Share by Race (2018–2024)",
        font: { size: 16, weight: "bold" },
        color: "#111827",
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.label}: ${ctx.parsed.y.toFixed(2)}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: -2,
        max: 1.5,
        ticks: { callback: (v) => v + "%", color: "#4B5563" },
        title: {
          display: true,
          text: "Percentage Change (%)",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563", autoSkip: false, maxRotation: 45 },
        title: {
          display: true,
          text: "Race Groups",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ✅ PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("percentage_change_by_race.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-green-50 via-white to-emerald-50 rounded-xl shadow-lg"
      >
        {/* Title */}
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Percentage Change in Employment Share by Race (2018–2024)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Highest Growth</p>
            <p className="text-lg font-bold text-green-800">
              {topIncrease.race} (+{topIncrease.change.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Largest Decline</p>
            <p className="text-lg font-bold text-red-800">
              {topDecrease.race} ({topDecrease.change.toFixed(2)}%)
            </p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Average Change</p>
            <p className="text-lg font-bold text-blue-800">{avgChange}%</p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="p-2">Race</th>
                  <th className="p-2">Percentage Change (%)</th>
                </tr>
              </thead>
              <tbody>
                {raceData.map((r) => (
                  <tr
                    key={r.race}
                    className={`hover:bg-gray-100 transition ${
                      r.change >= 0 ? "bg-green-50" : "bg-red-50"
                    }`}
                  >
                    <td className="p-2 font-medium">{r.race}</td>
                    <td
                      className={`p-2 font-semibold ${
                        r.change >= 0 ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {r.change > 0 ? "+" : ""}
                      {r.change.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📊 Between 2018 and 2024,{" "}
          <span className="font-semibold text-green-700">
            {topIncrease.race}
          </span>{" "}
          saw the highest rise (+{topIncrease.change.toFixed(2)}%), while{" "}
          <span className="font-semibold text-red-700">{topDecrease.race}</span>{" "}
          experienced the largest decline ({topDecrease.change.toFixed(2)}%).
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
